import { useMemo } from "react";

/**
 * One-shot gold-dust sparkle burst radiating from the center.
 * Renders ~48 particles with randomized angles, distances, sizes and delays.
 * All animation runs on transform/opacity (GPU-friendly).
 */
export default function ParticleBurst({ active }) {
  const particles = useMemo(
    () =>
      Array.from({ length: 48 }).map((_, i) => {
        const angle = Math.random() * Math.PI * 2;
        const distance = 90 + Math.random() * 180; // px from center
        const x = Math.cos(angle) * distance;
        const y = Math.sin(angle) * distance - 20; // gentle lift
        const size = 3 + Math.random() * 6;
        const delay = Math.random() * 180; // ms
        const duration = 1400 + Math.random() * 900;
        const color =
          i % 5 === 0
            ? "#F3E5AB"
            : i % 5 === 1
            ? "#FFF5D1"
            : i % 5 === 2
            ? "#D4AF37"
            : i % 5 === 3
            ? "#B98F1B"
            : "#E5C76B";
        const shape = i % 7 === 0 ? "star" : "dot";
        return { id: i, x, y, size, delay, duration, color, shape };
      }),
    []
  );

  if (!active) return null;

  return (
    <div
      aria-hidden="true"
      data-testid="sparkle-burst"
      className="pointer-events-none absolute inset-0 z-30 overflow-hidden"
    >
      {/* Central radial flash */}
      <span
        className="absolute top-1/2 left-1/2 w-40 h-40 -translate-x-1/2 -translate-y-1/2 rounded-full"
        style={{
          background:
            "radial-gradient(circle, rgba(255,245,200,0.9), rgba(212,175,55,0.4) 45%, transparent 70%)",
          filter: "blur(8px)",
          animation: "sparkle-flash 1100ms ease-out forwards",
        }}
      />
      {/* Ring shockwave */}
      <span
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-gold/70"
        style={{
          width: 40,
          height: 40,
          boxShadow: "0 0 40px rgba(212,175,55,0.6)",
          animation: "sparkle-ring 1600ms cubic-bezier(.22,1,.36,1) forwards",
        }}
      />
      {/* Particles */}
      {particles.map((p) => (
        <span
          key={p.id}
          className="absolute top-1/2 left-1/2"
          style={{
            width: p.size,
            height: p.size,
            background:
              p.shape === "dot"
                ? `radial-gradient(circle, ${p.color} 0%, ${p.color}88 60%, transparent 100%)`
                : "transparent",
            color: p.color,
            borderRadius: p.shape === "dot" ? "50%" : "0",
            filter: `drop-shadow(0 0 ${p.size * 1.5}px ${p.color}) blur(0.3px)`,
            transform: "translate(-50%, -50%)",
            animation: `sparkle-fly ${p.duration}ms cubic-bezier(.18,.7,.25,1) ${p.delay}ms forwards`,
            // CSS custom properties consumed by the keyframe
            "--px": `${p.x}px`,
            "--py": `${p.y}px`,
          }}
        >
          {p.shape === "star" && (
            <svg viewBox="0 0 24 24" width={p.size * 2.4} height={p.size * 2.4}>
              <path
                d="M12 2l1.8 6.4L20 10l-5.4 3.2L16 20l-4-4-4 4 1.4-6.8L4 10l6.2-1.6z"
                fill="currentColor"
              />
            </svg>
          )}
        </span>
      ))}
    </div>
  );
}
